import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { fieldLabel, fieldWrapper, inputBase, selectBase } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { fetchCalculationSettings } from '../api/calculation-settings.api'
import { createCalculation, fetchSchemes, lookupContacts, lookupUsers, type CreateCalculationPayload } from '../api/calculations.api'
import type { CalcLookupClient, CalcLookupContact, CalcLookupUser, CalcScheme, Calculation } from '../types/calculation.types'
import { PartnerAuswahl } from './PartnerAuswahl'

/** Heute plus n Tage als `JJJJ-MM-TT` (Ortszeit). */
function inTagen(tage: number): string {
  const d = new Date()
  d.setDate(d.getDate() + tage)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * AP-37 4.9 — **Neue Kalkulation: nur die Kalkulationsdaten.** Bezeichnung,
 * Kunde oder Interessent, Ansprechpartner, Bauvorhaben, Abgabetermin, Bindefrist,
 * Kalkulant, Zuschlagsschema. Ob eine oder mehrere Positionen oder ein LV,
 * entscheidet sich in der Kalkulation (Zeichenfläche 2). Knopf „Anlegen" (AP-36).
 *
 * Mit `vorlage` legt derselbe Dialog einen Vorlagenkasten an: ohne Kunde,
 * Abgabe und Bindefrist (4.17).
 */
export function CalculationCreateDialog({
  vorlage = false,
  onClose,
  onSaved,
}: {
  vorlage?: boolean
  onClose: () => void
  onSaved: (k: Calculation) => void
}) {
  const { user } = useAuthStore()
  const [name, setName] = useState('')
  const [art, setArt] = useState<'partner' | 'interessent'>('partner')
  const [partner, setPartner] = useState<CalcLookupClient | null>(null)
  const [kontakte, setKontakte] = useState<CalcLookupContact[]>([])
  const [kontakt, setKontakt] = useState('')
  const [interessent, setInteressent] = useState({ name: '', ort: '' })
  const [bauvorhaben, setBauvorhaben] = useState('')
  const [abgabeTag, setAbgabeTag] = useState('')
  const [abgabeZeit, setAbgabeZeit] = useState('')
  const [bindefrist, setBindefrist] = useState('')
  const [kalkulant, setKalkulant] = useState(user?.id ?? '')
  const [schema, setSchema] = useState('')
  const [benutzer, setBenutzer] = useState<CalcLookupUser[]>([])
  const [schemata, setSchemata] = useState<CalcScheme[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    lookupUsers().then(setBenutzer).catch(() => setBenutzer([]))
    fetchSchemes()
      .then((s) => {
        setSchemata(s)
        setSchema(s.find((x) => x.isDefault)?.id ?? s[0]?.id ?? '')
      })
      .catch(() => setSchemata([]))
    fetchCalculationSettings()
      .then((e) => setBindefrist(inTagen(e.validityDays ?? 30)))
      .catch(() => setBindefrist(inTagen(30)))
  }, [])

  useEffect(() => {
    setKontakt('')
    if (!partner) return setKontakte([])
    lookupContacts(partner.id)
      .then((k) => {
        setKontakte(k)
        setKontakt(k.find((x) => x.isDefault)?.id ?? '')
      })
      .catch(() => setKontakte([]))
  }, [partner])

  const anlegen = async () => {
    setFehler(null)
    if (!name.trim()) return setFehler('Bitte eine Bezeichnung eingeben.')
    const dto: CreateCalculationPayload = { name: name.trim(), isTemplate: vorlage || undefined }
    if (!vorlage) {
      if (art === 'partner' && partner) {
        dto.clientId = partner.id
        if (kontakt) dto.contactPersonId = kontakt
      }
      if (art === 'interessent' && interessent.name.trim()) {
        dto.prospectName = interessent.name.trim()
        if (interessent.ort.trim()) dto.prospectCity = interessent.ort.trim()
      }
      if (bauvorhaben.trim()) dto.siteName = bauvorhaben.trim()
      if (abgabeTag) dto.dueAt = new Date(`${abgabeTag}T${abgabeZeit || '00:00'}:00`).toISOString()
      if (bindefrist) dto.validUntil = bindefrist
    }
    if (kalkulant) dto.ownerUserId = kalkulant
    if (schema) dto.schemeId = schema
    setLaeuft(true)
    try {
      onSaved(await createCalculation(dto))
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Kalkulation ließ sich nicht anlegen.'))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-lg border border-border p-6 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-semibold text-primary">{vorlage ? 'Neue Vorlage' : 'Neue Kalkulation'}</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
        {fehler && <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className={`${fieldWrapper} sm:col-span-2`}>
            <label className={fieldLabel}>Bezeichnung</label>
            <input className={inputBase} autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={vorlage ? 'z. B. Geländer verzinkt je lfm' : 'z. B. Geländer Einfamilienhaus'} />
          </div>

          {!vorlage && (
            <div className={`${fieldWrapper} sm:col-span-2`}>
              <label className={fieldLabel}>Kunde</label>
              <div className="inline-flex gap-0.5 rounded-lg border border-border bg-muted/40 p-1 text-sm">
                {(
                  [
                    ['partner', 'aus Adressen'],
                    ['interessent', 'Interessent'],
                  ] as const
                ).map(([k, t]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setArt(k)}
                    className={`rounded-md px-3 py-1 ${art === k ? 'bg-primary font-medium text-primary-foreground' : 'text-muted-foreground'}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {art === 'partner' ? (
                <PartnerAuswahl wert={partner} onWahl={setPartner} klasse={inputBase} />
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <input className={inputBase} placeholder="Name" value={interessent.name} onChange={(e) => setInteressent((i) => ({ ...i, name: e.target.value }))} />
                  <input className={inputBase} placeholder="Ort" value={interessent.ort} onChange={(e) => setInteressent((i) => ({ ...i, ort: e.target.value }))} />
                </div>
              )}
            </div>
          )}

          {!vorlage && art === 'partner' && (
            <div className={fieldWrapper}>
              <label className={fieldLabel}>Ansprechpartner</label>
              <select className={selectBase} value={kontakt} onChange={(e) => setKontakt(e.target.value)} disabled={!partner}>
                <option value="">{partner ? '— niemand —' : 'erst Kunde wählen'}</option>
                {kontakte.map((k) => (
                  <option key={k.id} value={k.id}>
                    {[k.firstName, k.lastName].filter(Boolean).join(' ')}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!vorlage && (
            <div className={fieldWrapper}>
              <label className={fieldLabel}>Bauvorhaben</label>
              <input className={inputBase} value={bauvorhaben} onChange={(e) => setBauvorhaben(e.target.value)} />
            </div>
          )}
          {!vorlage && (
            <div className={fieldWrapper}>
              <label className={fieldLabel}>Abgabetermin</label>
              <div className="flex gap-2">
                <input type="date" className={inputBase} value={abgabeTag} onChange={(e) => setAbgabeTag(e.target.value)} />
                <input type="time" className={`${inputBase} w-28`} value={abgabeZeit} onChange={(e) => setAbgabeZeit(e.target.value)} aria-label="Uhrzeit" />
              </div>
            </div>
          )}
          {!vorlage && (
            <div className={fieldWrapper}>
              <label className={fieldLabel}>Bindefrist</label>
              <input type="date" className={inputBase} value={bindefrist} onChange={(e) => setBindefrist(e.target.value)} />
            </div>
          )}
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Kalkulant</label>
            <select className={selectBase} value={kalkulant} onChange={(e) => setKalkulant(e.target.value)}>
              {benutzer.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Zuschlagsschema</label>
            <select className={selectBase} value={schema} onChange={(e) => setSchema(e.target.value)}>
              {schemata.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 rounded-md bg-sky-500/10 px-3 py-2 text-sm text-sky-900 dark:text-sky-200">
          Nur die Kalkulationsdaten. Ob eine oder mehrere Positionen nötig sind oder ein LV zugrunde liegt, entscheidet sich in der
          Kalkulation. Sätze und Zuschlagsschema werden beim Anlegen übernommen.
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50" disabled={laeuft || !name.trim()} onClick={() => void anlegen()}>
            Anlegen
          </button>
        </div>
      </div>
    </div>
  )
}
